import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { LiaBirthdayCakeSolid } from 'react-icons/lia';

import { YearsHistory, History as HistoryInterface } from 'interfaces';

import { AppLocale, BAND_NAME, BAND_NAME_JP } from 'constant';
import { LanguageContext } from 'context/languageContext';

import { getAlbum, getSong, getConcert, getTour, getDvd, getAwardDetails, getAlbumNumber } from 'utils';

import Title from 'components/ui/Title';

import {
  Wrapper,
  StyledYearTitle,
  YearWrapper,
  YearBlock,
  HistoryWrapper,
  GuitarNeck,
  EventBlock,
  Arrow,
  StyledTitle,
  LegendList,
  LegendListItem,
  StyledDescriptionLink,
  AddDescription
} from './History.styles';

export interface PeopleProps {
  historyYear: YearsHistory;
  className?: string;
}

const History = ({ historyYear, className = '' }: PeopleProps) => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  const variables = {
    bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
    songIWasKing: (
      <StyledDescriptionLink to={getSong('i-was-king', locale, 'link')}>
        {getSong('i-was-king', locale, 'name')}
      </StyledDescriptionLink>
    )
  };

  const getDescription = (element: HistoryInterface, year: string) => {
    switch (element.type) {
      case 'concert': {
        return (
          <Link to={getConcert(element?.id, locale, intl, 'link')}>
            {intl.formatMessage({ id: 'history.concert' }, { ...variables, concert: getConcert(element?.id, locale, intl, 'name') })}
          </Link>
        );
      }
      case 'tour': {
        return (
          <Link to={getTour(element?.id, locale, 'link')}>
            {intl.formatMessage({ id: 'history.tour' }, { ...variables, tour: getTour(element?.id, locale, 'name') })}
          </Link>
        );
      }
      case 'single': {
        return intl.formatMessage({ id: 'history.songRelease' }, {
          ...variables,
          song: <StyledDescriptionLink to={getSong(element?.id, locale, 'link')}>{getSong(element?.id, locale, 'name')}</StyledDescriptionLink>
        });
      }
      case 'album': {
        return intl.formatMessage({ id: 'history.albumRelease' }, {
          ...variables,
          albumNumber: getAlbumNumber(element?.id, element?.isInternational),
          album: (
            <StyledDescriptionLink to={getAlbum(element?.id, element?.isInternational, locale, 'link')}>
              {getAlbum(element?.id, element?.isInternational, locale, 'name')}
            </StyledDescriptionLink>
          ),
          international: element.isInternational ? intl.formatMessage({ id: 'history.album.international' }) : ''
        })
        ;
      }
      case 'miniAlbum': {
        return intl.formatMessage({ id: 'history.miniAlbumRelease' }, {
          ...variables,
          albumNumber: getAlbumNumber(element?.id, element?.isInternational),
          miniAlbum: (
            <StyledDescriptionLink to={getAlbum(element?.id, element?.isInternational, locale, 'link')}>
              {getAlbum(element?.id, element?.isInternational, locale, 'name')}
            </StyledDescriptionLink>
          )
        });
      }
      case 'dvd': {
        return intl.formatMessage({ id: 'history.dvdRelease' }, {
          ...variables,
          dvd: (
            <StyledDescriptionLink to={getDvd(element?.id, locale, 'link')}>{getDvd(element?.id, locale, 'name')}</StyledDescriptionLink>
          )
        });
      }
      case 'award': {
        return intl.formatMessage({ id: 'history.awards' }, { ...variables, ...getAwardDetails(element?.id, locale, intl) });
      }
      case 'bandHistory':
      case 'memberBirthday': {
        return intl.formatMessage({ id: `history.${element?.id}${element.type === 'memberBirthday' ? '_birthday' : ''}` }, {
          yearsOld: Number(year) - Number(element.birthdayYear)
        });
      }
      default: {
        return `${element?.id}}`
      }
    }
  };

  return (
    <Wrapper className={className}>
      <Title size="big" color="secondary">
        {intl.formatMessage({ id: 'history.title' })}
      </Title>
      <LegendList>
        <LegendListItem colorType="tour">{intl.formatMessage({ id: 'history.legend.tour' })}</LegendListItem>
        <LegendListItem colorType="concert">{intl.formatMessage({ id: 'history.legend.concert' })}</LegendListItem>
        <LegendListItem colorType="single">{intl.formatMessage({ id: 'history.legend.single' })}</LegendListItem>
        <LegendListItem colorType="album">{intl.formatMessage({ id: 'history.legend.album' })}</LegendListItem>
        <LegendListItem colorType="dvd">{intl.formatMessage({ id: 'history.legend.dvd' })}</LegendListItem>
        <LegendListItem colorType="award">{intl.formatMessage({ id: 'history.legend.award' })}</LegendListItem>
        <LegendListItem colorType="bandHistory">{intl.formatMessage({ id: 'history.legend.bandHistory' })}</LegendListItem>
      </LegendList>
      <HistoryWrapper>
        <GuitarNeck>
          <span /><span /><span /><span /><span />
          <span /><span /><span /><span /><span />
          <span /><span /><span /><span /><span />
        </GuitarNeck>
        <YearWrapper>
          {
            Object.entries(historyYear)
              .map(([year, history], index) => (
                <YearBlock key={year}>
                  <StyledYearTitle color={(index + 1) % 2 ? 'primary' : 'secondary'}>{year}</StyledYearTitle>
                  {
                    history.map((element) => (
                      <EventBlock key={`${year}-${element?.id}${element.isInternational ? '-international' : ''}${element.type === 'dvd' ? '-dvd' : ''}`}>
                        <Arrow colorType={element.type} />
                        <StyledTitle size="small" noMargin color={(index + 1) % 2 ? 'primary' : 'secondary'}>
                          {intl.formatDate(element.date, {
                            year: 'numeric', month: 'long',
                            ...(element.isDateWithoutDay ? {} : { day: 'numeric' })
                          })}
                          {
                            element.type === 'memberBirthday' ? (
                              <LiaBirthdayCakeSolid />
                            ) : null
                          }
                        </StyledTitle>
                        <p>
                          {getDescription(element, year)}
                        </p>
                        {
                          element?.hasHistoryDescription ? (
                              <AddDescription>{intl.formatMessage({ id: `history.additionalDescription.${element?.id}` }, { ...variables })}</AddDescription>
                            ) : null
                        }
                      </EventBlock>
                    ))
                  }
                </YearBlock>
              ))
          }
        </YearWrapper>
      </HistoryWrapper>
    </Wrapper>
  );
};

export default History;
